import { getOffsetLimitFromPagination, getQueryString } from '../utils/query';
import { PagedRequestArgs, PagedResponse } from '../utils/types';
import { callApi } from './api';
import { PermissionMR } from './permission';
import { RoleR } from './roles';

/**
 * Coarse category. Derived from `authMethods` once anything is established;
 * on a pending invite it holds the category the admin picked. `both` (password
 * + at least one SSO provider) is never assigned directly.
 */
export type UserAuthType = 'password' | 'sso' | 'both';

/** A concrete way the user can sign in. */
export type AuthMethod = 'password' | 'google' | 'facebook';

export const AUTH_METHOD_LABELS: Record<AuthMethod, string> = {
  password: 'Password',
  google: 'Google',
  facebook: 'Facebook'
};

export type UserRole = {
  role: RoleR;
  condition: Record<string, any> | null; // Object or null
};

export type UserRoleInput = {
  roleId: number;
  condition: Record<string, any> | null;
};

export type UserR = {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  legalEmail?: string;
  isActive: boolean;
  slackId?: string;
  avatar?: File;
  createdDatetime: string;
  createdById?: number;
  roles: UserRole[];
  authType?: null | UserAuthType;
  // Every sign-in method actually established for this user. Empty while an
  // invited account is still pending.
  authMethods: AuthMethod[];
  // Per-user whitelist of domains allowed as redirect_url when this user acts
  // as a client/service. Only settable by IAM superusers.
  allowedRedirectDomains?: string[] | null;
};

export type UserC = {
  email: string;
  firstName: string;
  lastName: string;
  legalEmail?: string;
  slackId?: string;
  avatar?: File;
  authType?: null | UserAuthType;
  roles: UserRoleInput[];
  allowedRedirectDomains?: string[];
};

export type UserU = {
  email: string;
  firstName: string;
  lastName: string;
  legalEmail?: string;
  slackId?: string;
  avatar?: File;
  authType?: null | UserAuthType;
  roles: UserRoleInput[];
  allowedRedirectDomains?: string[];
};

export type UserPermission = Omit<PermissionMR, 'params'> & {
  params?: Record<string, any>;
};

export type UserWithPermissions = UserR & {
  permissions: UserPermission[];
};

export type AvailablePlatform = {
  key: string;
  url: string;
};

class UsersService {
  async getPaged({
    page,
    size,
    cancelToken,
    searchValue
  }: PagedRequestArgs & { searchValue: string }): Promise<PagedResponse<UserR>> {
    const query = getQueryString({ ...getOffsetLimitFromPagination({ page, size }), search: searchValue });
    return callApi({ url: `/users/?${query}`, cancelToken });
  }

  async getAll(): Promise<UserR[]> {
    return callApi({ url: '/users/' });
  }

  async getUser(id: number): Promise<UserWithPermissions> {
    return callApi({ url: `/users/${id}/with_permissions/` });
  }

  async add(data: UserC) {
    const {
      avatar,
      email,
      firstName,
      lastName,
      legalEmail,
      slackId,
      authType,
      roles = [],
      allowedRedirectDomains
    } = data;

    const formData = new FormData();

    formData.append('email', email);
    formData.append('firstName', firstName);
    formData.append('lastName', lastName);
    legalEmail && formData.append('legalEmail', legalEmail);
    slackId && formData.append('slackId', slackId);

    // Handle roles with conditions - send as single JSON array
    if (roles.length > 0) {
      formData.append('roles', JSON.stringify(roles));
    }

    // `allowedRedirectDomains` is a list[str] form field on the backend, so it
    // must be sent as repeated fields (one entry per append).
    if (allowedRedirectDomains) {
      allowedRedirectDomains.forEach((domain) => {
        formData.append('allowedRedirectDomains', domain);
      });
    }

    formData.append('authType', authType ?? '');

    if (avatar) {
      formData.append('avatar', avatar, avatar.name);
    }

    return callApi({ url: '/users/', method: 'POST', data: formData });
  }

  async edit(id: number, data: UserU) {
    const {
      avatar,
      email,
      firstName,
      lastName,
      legalEmail,
      slackId,
      roles = [],
      authType,
      allowedRedirectDomains
    } = data;
    const formData = new FormData();
    formData.append('email', email);
    formData.append('firstName', firstName);
    formData.append('lastName', lastName);

    legalEmail && formData.append('legalEmail', legalEmail);
    slackId && formData.append('slackId', slackId);

    // Handle roles with conditions - send as single JSON array
    if (roles.length > 0) {
      formData.append('roles', JSON.stringify(roles));
    }

    // `allowedRedirectDomains` is a list[str] form field on the backend, so it
    // must be sent as repeated fields (one entry per append). Sending none
    // clears the whitelist (backend only honors this for IAM superusers).
    if (allowedRedirectDomains) {
      allowedRedirectDomains.forEach((domain) => {
        formData.append('allowedRedirectDomains', domain);
      });
    }

    formData.append('authType', authType ?? '');

    if (avatar && avatar instanceof File) {
      formData.append('avatar', avatar, avatar.name);
    }

    return callApi({ url: `/users/${id}/`, method: 'PUT', data: formData });
  }

  async deactivate(id: number) {
    return callApi({ url: `/users/${id}/deactivate/`, method: 'POST' });
  }

  async activate(id: number) {
    return callApi({ url: `/users/${id}/activate/`, method: 'POST' });
  }

  async resendSetupMail(id: number) {
    return callApi({ url: `/users/${id}/resend_setup_mail/`, method: 'POST' });
  }

  async resendSetupMailWithUserMail(email: string) {
    return callApi({
      url: '/auth/users/resend_setup_mail/',
      method: 'POST',
      data: {
        email: email
      },
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  async getAvailablePlatforms(): Promise<AvailablePlatform[]> {
    return callApi({ url: '/users/me/available_platforms' });
  }
}

export const usersService = new UsersService();
